/* eslint-disable no-useless-rename */
import { SagaIterator } from '@redux-saga/core'
import { all, put, call, takeLatest } from 'redux-saga/effects'
import login from 'services/login'
import { setError, setLoading, setUser, userLoginSagas } from 'ducks/user/reducer'
import { TOKEN } from 'constants/global.constants'
import type { IMfaChallengeSession } from 'services/login/mfa-session'
import { MFA_LOGIN_STORAGE_KEY } from 'services/login/mfa-session'

interface IHttpError {
  response?: {
    status?: number
    data?: {
      code?: string
      message?: string
    }
  }
  message?: string
}

function* loginSaga(action: any): SagaIterator {
  yield put(setLoading(true))

  try {
    const response = yield call(login, action?.payload?.email, action?.payload?.password)
    const user = response?.data

    if (user?.token) {
      const {
        company_id: companyId,
        token,
        user_name: userName,
        has_admin_platform_management: hasAdminPlatformManagement,
        has_questionnaire_management: hasQuestionnaireManagement,
      } = user

      yield put(
        setUser({
          email: action?.payload?.email,
          token,
          companyId,
          userName,
          hasAdminPlatformManagement,
          hasQuestionnaireManagement,
        })
      )
      localStorage.setItem(TOKEN, token)
      if (user?.message) {
        yield put(
          setError({
            error: true,
            message: user.message,
            code: 'MFA_STAGING_BYPASS',
          })
        )
      }
      yield put(setLoading(false))
    } else if (user?.mfa_required && user?.challenge_token) {
      const challenge: IMfaChallengeSession = {
        challengeToken: user.challenge_token,
        email: user.email || action?.payload?.email || '',
        userName: user.user_name || '',
        companyId: user.company_id || 0,
        role: user.role || '',
        canRememberDevice: Boolean(user.can_remember_device),
        lastAccessAt: user.last_login_at || user.last_access_at || user.lastLoginAt || user.lastAccessAt,
        expiresInMinutes: user.expires_in_minutes || 10,
        sentAt: new Date().toISOString(),
        resendCount: 0,
        attemptsLeft: user.attempts_left ?? 5,
      }

      sessionStorage.setItem(MFA_LOGIN_STORAGE_KEY, JSON.stringify(challenge))
      yield put(setLoading(false))
      window.location.href = '/login/mfa'
      return
    } else {
      const message = user?.message || 'Credentials are invalid'
      yield put(
        setError({
          error: true,
          message,
          code: String(response?.status || 500),
        })
      )
      yield put(setLoading(false))
    }
  } catch (error) {
    const httpError = error as IHttpError
    const status = httpError.response?.status || 500
    const message = httpError.response?.data?.message || httpError.message || 'Credentials are invalid'

    yield put(
      setError({
        error: true,
        message,
        code: httpError.response?.data?.code || String(status),
      }),
    )
    yield put(setLoading(false))
  } finally {
    yield put(setLoading(false))
  }
}

export function* userSagas(): SagaIterator {
  yield all([takeLatest(userLoginSagas.trigger, loginSaga)])
}
